import React from 'react';

import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { ListItem, ListItemText, Typography, IconButton, Hidden } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import MenuIcon from '@mui/icons-material/Menu';
import BackIcon from '@mui/icons-material/ArrowBack';
import classNames from 'classnames';
import { openSideBar } from 'actions';
import history from 'services/history';
import { HEADER_BAR_BACK_ACTION } from './targets';
import UniquePortal from './UniquePortal';
import { useTranslation } from 'services/localization/useTranslation';

import { push, isRouteActive } from 'routing';

interface BackAction {
	title: string;
	to?: string | null;
	onClick?: () => void;
}

interface QuickLink {
	title: string;
	to: string;
	onClick?: () => void;
}

interface Props {
	backAction?: BackAction;
	quickLinks?: QuickLink[];
	priority?: number;
}

const SetPageNavAction = React.memo((props: Props) => {
	const dispatch = useDispatch();
	const classes = useStyles();
	const location = useLocation();
	const { t } = useTranslation();

	const renderHamburger = () => {
		return (
			<IconButton
				onClick={() => dispatch(openSideBar())}
				aria-label={t('headerBar.menu', 'Menu')}
				color="inherit"
				size="large"
			>
				<MenuIcon />
			</IconButton>
		);
	};

	const renderQuickLinks = () => {
		return (
			<>
				<IconButton
					onClick={() => dispatch(openSideBar())}
					onMouseDown={(e) => e.preventDefault()}
					aria-label={t('headerBar.menu', 'Menu')}
					color="inherit"
					size="large"
				>
					<MenuIcon />
				</IconButton>
				<Hidden smDown>
					{props.quickLinks &&
						props.quickLinks.map(({ title, to, onClick }) => (
							<ListItem
								key={`${title}${to}`}
								button
								onClick={() => {
									push(to);
									onClick?.();
								}}
								onMouseDown={(e) => e.preventDefault()}
								className={classNames(classes.quickLinksButton, {
									[classes.quickLinksButtonActive]: isRouteActive(to, location.pathname),
								})}
							>
								<ListItemText
									primary={title}
									primaryTypographyProps={{
										className: classes.quickLinksButtonText,
										variant: 'caption',
									}}
								/>
							</ListItem>
						))}
				</Hidden>
			</>
		);
	};

	const renderBackAction = () => {
		const backAction = props.backAction!;

		return (
			<>
				<IconButton
					onClick={() => {
						backAction.onClick?.();
						if (!!backAction.to) {
							history.replace(backAction.to as string);
						} else {
							history.goBack();
						}
					}}
					color="inherit"
					aria-label={t('common:actions.back')}
					size="large"
				>
					<BackIcon />
				</IconButton>
				<Typography variant="h6" className={classes.backText}>
					{backAction.title}
				</Typography>
			</>
		);
	};

	return (
		<UniquePortal target={HEADER_BAR_BACK_ACTION} priority={props.priority || 0}>
			{props.backAction
				? renderBackAction()
				: props.quickLinks
				? renderQuickLinks()
				: renderHamburger()}
		</UniquePortal>
	);
});

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		quickLinksButton: {
			padding: theme.spacing(0, 3),
			flex: 0,
			alignSelf: 'stretch',
		},
		quickLinksButtonText: {
			textTransform: 'uppercase',
			whiteSpace: 'nowrap',
			fontWeight: 500,
		},
		quickLinksButtonActive: {
			backgroundColor: theme.palette.action.selected,
		},
		backText: {
			fontWeight: 500,
			fontSize: '1.8rem',
		},
	}),
);

export default SetPageNavAction;
